//@flow
import { CommonActions } from '@react-navigation/native';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import {
  fetchDetailedUsagesSuccess,
  fetchUsageAggregateSuccess,
  fetchUsagesSuccess,
  resetUsage,
  UsagesActionType,
} from '../../actions/usage';
import trpcApi, { tripica } from '../../api';
import config from '../../config';
import { extractIddZone } from '../../lib/IddZoneExtractor';
import { injectMockRoamingUsages } from '../../lib/mockRoamingOptionsUsages';
import Navigation from '../../lib/navigation';
import { canChangePlanOrOptionsOfCurrentSubscriptionSelector, currentSubscriptionIdSelector } from '../Dashboard';
import { goToInternationalTopUpOfZone } from '../International/IDD';
import { setLoading } from '../LoadingStatus';
import type { Action } from './actions';
import WizzeeLog from '../../lib/wizzeeLog';

export function* selectUsageSaga(action: Action): Generator<*, *, *> {
  if (!config.skipYourUsagePage) {
    yield call(Navigation.dispatch, CommonActions.navigate({ name: 'yourUsage' }));
    return;
  }

  if (!(yield select(canChangePlanOrOptionsOfCurrentSubscriptionSelector))) return;
  const { usage } = action.payload;
  if (usage && usage.label.includes('IDD')) {
    const iddZone = extractIddZone(usage.label);
    iddZone
      ? yield put(goToInternationalTopUpOfZone(iddZone))
      : yield call(Navigation.dispatch, CommonActions.navigate({ name: 'international' }));
  } else {
    yield call(Navigation.dispatch, CommonActions.navigate({ name: 'topUp' }));
  }
}

export function* fetchDetailedUsagesSaga(action: UsagesActionType): Generator<*, *, *> {
  const { subscriptionOuid, beginDate, endDate } = action.payload;

  yield put(setLoading('fetchDetailedUsages', true));

  try {
    let offerId = subscriptionOuid;

    if (!subscriptionOuid) {
      offerId = yield select(currentSubscriptionIdSelector);
    }

    const detailedUsages = yield call([trpcApi, 'fetchUsages'], offerId, beginDate, endDate);

    yield put(fetchDetailedUsagesSuccess(detailedUsages));
  } catch (error) {
    WizzeeLog.warn('[saga] fetchDetailedUsagesSaga', error);
  } finally {
    yield put(setLoading('fetchDetailedUsages', false));
  }
}

export function* fetchUsageAggregateSaga(action: UsagesActionType): Generator<*, *, *> {
  const { subscriptionOuid, period, beginDate, endDate } = action.payload;

  yield put(setLoading('fetchUsageAggregate', true));

  try {
    let offerId = subscriptionOuid;

    if (!subscriptionOuid) {
      offerId = yield select(currentSubscriptionIdSelector);
    }

    const usageAggregate = yield call([trpcApi, 'fetchUsageAggregate'], offerId, period, beginDate, endDate);

    yield put(fetchUsageAggregateSuccess(usageAggregate));
  } catch (error) {
    WizzeeLog.warn('[saga] fetchUsageAggregateSaga', error);
  } finally {
    yield put(setLoading('fetchUsageAggregate', false));
  }
}

export function* fetchUsagesSaga(action: UsagesActionType): Generator<*, *, *> {
  const { line } = action.payload;

  yield put(setLoading('fetchUsages', true));

  try {
    let usages = yield call([tripica, 'getUsages'], line);
    usages = injectMockRoamingUsages(usages);

    yield put(fetchUsagesSuccess(usages));
  } catch (error) {
    // Usage data is non-critical: a failure must not block the dashboard.
    // The UsageFrame handles empty data gracefully; we don't surface the
    // network-failure banner that would replace the entire dashboard content.
    WizzeeLog.warn('[saga] fetchUsagesSaga failed, continuing without usage data', error);
  } finally {
    yield put(setLoading('fetchUsages', false));
  }
}

export function* redirectAfterUsageFrameOnTitlePressSaga(action: UsagesActionType): Generator<*, *, *> {
  if (config.skipYourUsagePage) {
    yield call(Navigation.dispatch, CommonActions.navigate({ name: 'topUp' }));
  } else {
    // TODO remove config.skipYourUsagePage when usage graphs are done
    yield call(Navigation.dispatch, CommonActions.navigate({ name: 'yourUsage' }));
    yield put(resetUsage());
  }
}

export default function* usageSaga(): Generator<*, *, *> {
  yield takeEvery('SELECT_USAGE', selectUsageSaga);
  yield takeEvery('FETCH_DETAILED_USAGES', fetchDetailedUsagesSaga);
  yield takeEvery('FETCH_USAGE_AGGREGATE', fetchUsageAggregateSaga);
  yield takeEvery('FETCH_USAGES', fetchUsagesSaga);
  yield takeEvery('REDIRECT_AFTER_USAGE_FRAME_ON_TITLE_PRESS', redirectAfterUsageFrameOnTitlePressSaga);
}
